/* Sellaseal sash window bead calculator.
   Part 1 (SSBeadSolver) is pure maths with no DOM, so it can be tested in Node.
   Part 2 is the page UI. Every price, variant ID and stock flag comes from the
   section's JSON island, which Liquid renders from live products. Nothing here
   holds a price. */
(function (root, factory) {
  var S = factory();
  if (typeof module === 'object' && module.exports) module.exports = S;
  else root.SSBeadSolver = S;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  // Allowances in mm. Beads are cut to the measured opening (long point of the mitre), per Jack 19 Oct 2026:
  // no extra for mitres, and the head parting bead gets 10mm to sit into the rebate. Plus the saw cut and squaring the end.
  var A = { trim: 0, partJamb: 0, partHead: 10, kerf: 3, endSquare: 10, sealEnd: 100, sealWaste: 0.05 };
  var KIT = { dh: { w: 1000, h: 2000 }, casement: { w: 800, h: 800 }, door: { w: 900, h: 2200 } };
  var PILE = { '3-4': '5.5', '5-7': '8.5', '8-10': '11.5', unsure: '8.5' };

  // ---- Lengths: "900", "90cm", "1.2m", "35 1/2in", '35.5"' -> mm (or NaN)
  function parseLen(raw) {
    if (raw == null) return NaN;
    var s = String(raw).trim().toLowerCase().replace(/,/g, '.');
    if (!s) return NaN;
    var m = s.match(/^(\d+(?:\.\d+)?)(?:\s+(\d+)\/(\d+))?\s*(mm|cm|m|in|inch|inches|"|'')?$/);
    if (!m) return NaN;
    var v = parseFloat(m[1]);
    if (m[2]) v += parseInt(m[2], 10) / parseInt(m[3], 10);
    var u = m[4] || 'mm';
    var k = u === 'cm' ? 10 : u === 'm' ? 1000 : (u === 'mm' ? 1 : 25.4);
    return Math.round(v * k);
  }

  // ---- Pieces per window. W = frame opening width, H = sill to head, mm.
  function cutList(win, o) {
    var W = win.w, H = win.h, out = { staff: [], parting: [], perim: [], runs: {} };
    var d = o.system === 'a10' ? 20 : o.depth;
    function push(k, len, n, tag) { for (var i = 0; i < n; i++) out[k].push({ len: Math.ceil(len), tag: tag, win: win.label }); }
    if (win.type === 'dh') {
      push('staff', H + A.trim, 2, 'Side staff bead');
      push('staff', W + A.trim, 1, 'Head staff bead');
      push('staff', W + A.trim, 1, 'Bottom staff bead');
      var jamb = H + A.partJamb, cap = 3000 - A.endSquare - A.kerf;
      if (jamb <= cap) push('parting', jamb, 2, 'Side parting bead (cut at meeting rail)');
      else {
        push('parting', H / 2 + A.partJamb, 2, 'Lower side parting bead');
        push('parting', H / 2 + A.partJamb, 2, 'Upper side parting bead (flipped)');
      }
      push('parting', W + A.partHead, 1, 'Head parting bead');
      out.runs = { staffSeal: [H, H, W, W], partingSeal: [H, H, W], meeting: [W] };
    } else if (win.type === 'casement') {
      push('perim', H + A.trim, 2, 'Side seal');
      push('perim', W + A.trim, 2, 'Top and bottom seal');
      out.runs = { perimeter: [H, H, W, W] };
    }
    return out;
  }

  // Metres to order for a set of seal runs: net + 100mm per run, + 5%, whole metres.
  function sealMetres(runs) {
    if (!runs.length) return 0;
    var mm = runs.reduce(function (s, r) { return s + r + A.sealEnd; }, 0) * (1 + A.sealWaste);
    return Math.ceil(mm / 1000);
  }

  // ---- Cutting stock (no splicing). Lower bound, FFD, exact search, then MBS + restarts.
  function lowerBound(sizes, cap) {
    var sum = 0, big = 0;
    sizes.forEach(function (x) { sum += x; if (x > cap / 2) big++; });
    return Math.max(Math.ceil(sum / cap), big);
  }
  function ffd(sizes, cap, order) {
    var bins = [];
    order.forEach(function (i) {
      var s = sizes[i], b = null;
      for (var k = 0; k < bins.length; k++) if (bins[k].free >= s) { b = bins[k]; break; }
      if (!b) { b = { free: cap, items: [] }; bins.push(b); }
      b.free -= s; b.items.push(i);
    });
    return bins;
  }
  function exact(sizes, cap, ub, nodeCap) {
    nodeCap = nodeCap || 200000;
    var idx = sizes.map(function (_, i) { return i; }).sort(function (a, b) { return sizes[b] - sizes[a]; });
    var nodes = 0;
    function tryK(k) {
      var free = [], asg = [];
      for (var i = 0; i < k; i++) free.push(cap);
      function dfs(j) {
        if (++nodes > nodeCap) throw new Error('cap');
        if (j === idx.length) return true;
        var s = sizes[idx[j]], seen = {};
        for (var b = 0; b < k; b++) {
          if (free[b] < s || seen[free[b]]) continue;
          seen[free[b]] = 1;
          free[b] -= s; asg[j] = b;
          if (dfs(j + 1)) return true;
          free[b] += s;
          if (free[b] === cap) break;
        }
        return false;
      }
      if (!dfs(0)) return null;
      var bins = [];
      for (var q = 0; q < k; q++) bins.push({ free: cap, items: [] });
      asg.forEach(function (b, j) { bins[b].items.push(idx[j]); bins[b].free -= sizes[idx[j]]; });
      return bins;
    }
    var lb = lowerBound(sizes, cap);
    try {
      for (var k = lb; k < ub; k++) { var r = tryK(k); if (r) return { bins: r, proven: true }; }
    } catch (e) { return { bins: null, proven: false }; }
    return { bins: null, proven: true };
  }
  function mbs(sizes, cap) {
    var left = sizes.map(function (_, i) { return i; }).sort(function (a, b) { return sizes[b] - sizes[a]; });
    var bins = [];
    while (left.length) {
      var first = left[0], room = cap - sizes[first], rest = left.slice(1);
      var from = new Int32Array(room + 1).fill(-2), prev = new Int32Array(room + 1);
      from[0] = -1;
      for (var j = 0; j < rest.length; j++) {
        var s = sizes[rest[j]];
        for (var c = room; c >= s; c--) if (from[c] === -2 && from[c - s] !== -2) { from[c] = j; prev[c] = c - s; }
      }
      var cc = room; while (from[cc] === -2) cc--;
      var take = {}; take[first] = 1;
      while (cc > 0) { take[rest[from[cc]]] = 1; cc = prev[cc]; }
      var items = Object.keys(take).map(Number), used = 0;
      items.forEach(function (i) { used += sizes[i]; });
      bins.push({ free: cap - used, items: items });
      left = left.filter(function (i) { return !take[i]; });
    }
    return bins;
  }
  // Same cuts give the same plan, so re-renders (mode toggle, picker) are instant.
  var memo = {}, memoKeys = [];
  function solve(pieces, stockLen) {
    var key = stockLen + ':' + pieces.map(function (p) { return p.len; }).join(',');
    var hit = memo[key];
    if (hit) return relabel(hit, pieces);
    var r = solveRaw(pieces, stockLen);
    memo[key] = r; memoKeys.push(key);
    if (memoKeys.length > 40) delete memo[memoKeys.shift()];
    return r;
  }
  // Labels can change without the lengths changing: map cached cuts back to the new pieces.
  function relabel(r, pieces) {
    var pool = {};
    pieces.forEach(function (p) { (pool[p.len] = pool[p.len] || []).push(p); });
    var take = function (c) { var a = pool[c.len]; return a && a.length ? a.shift() : c; };
    return { bars: r.bars, lowerBound: r.lowerBound, proven: r.proven,
      plan: r.plan.map(function (b) { return { cuts: b.cuts.map(take), offcut: b.offcut }; }),
      oversize: r.oversize.map(take) };
  }
  function solveRaw(pieces, stockLen) {
    var cap = stockLen - A.endSquare, ok = [], oversize = [];
    pieces.forEach(function (p) { (p.len + A.kerf <= cap ? ok : oversize).push(p); });
    var sizes = ok.map(function (p) { return p.len + A.kerf; });
    if (!sizes.length) return { bars: 0, lowerBound: 0, proven: true, plan: [], oversize: oversize };
    var dec = sizes.map(function (_, i) { return i; }).sort(function (a, b) { return sizes[b] - sizes[a]; });
    var bins = ffd(sizes, cap, dec), lb = lowerBound(sizes, cap), proven = bins.length === lb;
    if (!proven && sizes.length <= 60) {
      var r = exact(sizes, cap, bins.length);
      if (r.bins) bins = r.bins;
      proven = r.proven;
    }
    if (!proven) {
      var b2 = mbs(sizes, cap);
      if (b2.length < bins.length) bins = b2;
      var seed = 1, rnd = function () { seed = (seed * 16807) % 2147483647; return seed / 2147483647; };
      for (var t = 0, tries = sizes.length > 300 ? 120 : 400; t < tries && bins.length > lb; t++) {
        var o = dec.slice();
        for (var i = 0; i < o.length - 1; i++) if (rnd() < 0.3) { var x = o[i]; o[i] = o[i + 1]; o[i + 1] = x; }
        var b3 = ffd(sizes, cap, o);
        if (b3.length < bins.length) bins = b3;
      }
      proven = bins.length === lb;
    }
    var plan = bins.map(function (b) {
      var cuts = b.items.map(function (i) { return ok[i]; }).sort(function (a, b) { return b.len - a.len; });
      return { cuts: cuts, offcut: Math.max(0, b.free) };
    });
    return { bars: bins.length, lowerBound: lb, proven: proven, plan: plan, oversize: oversize };
  }

  // ---- Cheapest way to buy at least `need` units from live offers {qty, price(cents), available}.
  function cheapestCover(need, offers) {
    offers = (offers || []).filter(function (o) { return o.available && o.price > 0 && o.qty > 0; });
    if (need <= 0) return { cost: 0, units: 0, spare: 0, buy: [] };
    if (!offers.length) return null;
    var maxQ = Math.max.apply(null, offers.map(function (o) { return o.qty; }));
    var N = need + maxQ, cost = [], pick = [];
    for (var q = 0; q <= N; q++) { cost.push(Infinity); pick.push(-1); }
    cost[0] = 0;
    for (q = 1; q <= N; q++) offers.forEach(function (o, k) {
      var c = cost[Math.max(0, q - o.qty)] + o.price;
      if (c < cost[q]) { cost[q] = c; pick[q] = k; }
    });
    var best = need;
    for (var x = need; x <= N; x++) if (cost[x] < cost[best]) best = x;
    var buy = {}, r = best;
    while (r > 0) { var o = offers[pick[r]]; buy[o.id] = buy[o.id] || { offer: o, n: 0 }; buy[o.id].n++; r = Math.max(0, r - o.qty); }
    var lines = Object.keys(buy).map(function (k) { return buy[k]; }).sort(function (a, b) { return b.offer.qty - a.offer.qty; });
    var units = lines.reduce(function (s, l) { return s + l.n * l.offer.qty; }, 0);
    return { cost: cost[best], units: units, spare: units - need, buy: lines };
  }

  // Units per variant from its option values: "10 (Pack)" -> 10, "Tube of 30" -> 30,
  // "200 metres" / "10m" -> metres, "Per Lineal Metre (1m)" -> 1, "20mm Deep" ignored.
  function packQty(options) {
    for (var i = (options || []).length - 1; i >= 0; i--) {
      var v = String(options[i]).toLowerCase().trim();
      if (/\d\s*mm\b/.test(v)) continue;
      var m;
      if (/per\s+lineal\s+met/.test(v)) return 1;
      if ((m = v.match(/(\d+(?:\.\d+)?)\s*(m|metres?|meters?)\b/))) return parseFloat(m[1]);
      if ((m = v.match(/tube of\s*(\d+)/))) return parseInt(m[1], 10);
      if ((m = v.match(/^(\d+)\b/))) return parseInt(m[1], 10);
    }
    return 1;
  }

  // Offers for one material, keeping variants whose options contain every `want` string.
  function offers(mat, want) {
    if (!mat) return [];
    want = (want || []).filter(Boolean).map(function (w) { return String(w).toLowerCase(); });
    return mat.variants.filter(function (v) {
      var hay = v.options.join(' / ').toLowerCase();
      return want.every(function (w) { return hay.indexOf(w) !== -1; });
    }).map(function (v) {
      return { id: v.id, qty: packQty(v.options), price: v.price, available: !!v.available, stock: v.stock, title: v.title };
    });
  }

  // ---- The whole job.
  function plan(job, data) {
    var o = job.opt, mats = {};
    (data.materials || []).forEach(function (m) { if (!mats[m.role]) mats[m.role] = m; });
    var colour = o.finish === 'natural' ? 'brown' : 'white';
    var pile = PILE[o.gap] || '8.5';
    var wins = [], issues = [];
    job.windows.forEach(function (w, i) {
      var W = parseLen(w.w), H = parseLen(w.h), qty = Math.max(1, Math.min(99, parseInt(w.qty, 10) || 1));
      var label = (w.label || '').trim() || ('Window ' + (i + 1));
      if (!(W > 0) || !(H > 0)) return;
      if (W < 150 || H < 150 || W > 6000 || H > 6000) { issues.push(label + ' is left out until its size looks right (' + W + ' x ' + H + 'mm).'); return; }
      for (var q = 0; q < qty; q++) wins.push({ label: qty > 1 ? label + ' (' + (q + 1) + '/' + qty + ')' : label, row: i, type: w.type || 'dh', w: W, h: H });
    });
    var dh = wins.filter(function (w) { return w.type === 'dh'; });
    var cas = wins.filter(function (w) { return w.type === 'casement'; });
    var doors = wins.filter(function (w) { return w.type === 'door'; });

    function kitFits(w) {
      var k = KIT[w.type];
      return k && w.w <= k.w && w.h <= k.h && o.finish === 'painted';
    }
    var dhKitOk = function (w) {
      return kitFits(w) && o.system === 'brush' && o.parting === 'timber' && o.depth === 20 && (o.gap === '5-7' || o.gap === 'unsure');
    };

    function partsDH(list) {
      var lines = [], plans = [], notes = [];
      if (!list.length) return { lines: lines, plans: plans, notes: notes };
      var staff = [], parting = [], staffRuns = [], partRuns = [], meetRuns = [];
      list.forEach(function (w) {
        var c = cutList(w, o);
        staff = staff.concat(c.staff); parting = parting.concat(c.parting);
        staffRuns = staffRuns.concat(c.runs.staffSeal); partRuns = partRuns.concat(c.runs.partingSeal);
        if (o.meeting) meetRuns = meetRuns.concat(c.runs.meeting);
      });
      var spare = o.spare ? Math.ceil(list.length / 10) : 0;
      var staffRole = o.system === 'a10' ? 'staff_bead_a10' : 'staff_bead';
      var staffMat = mats[staffRole], staffLen = (staffMat && staffMat.lengthMm) || (o.system === 'a10' ? 4200 : 3000);
      var sp = solve(staff, staffLen);
      plans.push({ key: 'staff', title: staffMat ? staffMat.title : 'Staff bead', stock: staffLen, res: sp });
      lines.push(beadLine(staffMat, o.system === 'a10' ? [] : [o.depth + 'mm'], sp.bars + spare, spare,
        'Staff bead: 2 sides, the head and the bottom of each window', sp, staffLen));
      if (sp.oversize.length) notes.push({ warn: true, text: sp.oversize.length + ' staff bead piece(s) are longer than one ' + (staffLen / 1000).toFixed(1) + 'm length. ' + (o.system === 'brush' ? 'Choose the compression system (4.2m bead) or call us.' : 'Call us: these need a joint.') });
      var partRole = o.parting === 'plastic' ? 'parting_bead_plastic' : 'parting_bead';
      var partMat = mats[partRole], partLen = (partMat && partMat.lengthMm) || 3000;
      var pp = solve(parting, partLen);
      plans.push({ key: 'parting', title: partMat ? partMat.title : 'Parting bead', stock: partLen, res: pp });
      lines.push(beadLine(partMat, [], pp.bars + spare, spare, 'Parting bead: 2 sides and the head', pp, partLen));
      if (pp.oversize.length) notes.push({ warn: true, text: pp.oversize.length + ' parting bead piece(s) are too long for one length. Call us.' });
      // Seals
      var brushRuns = o.system === 'a10' ? partRuns : staffRuns.concat(partRuns);
      var brushM = sealMetres(brushRuns), meetM = sealMetres(meetRuns);
      lines.push(meteredLine(mats.brush, [colour, pile + 'mm'], brushM + meetM,
        'Brush seal, ' + pile + 'mm pile, ' + colour + (meetM ? ': beads and meeting rail' : ': in the bead tracks')));
      if (meetM) lines.push(meteredLine(mats.carrier, [colour], meetM, 'Carrier track for the meeting rail seal (routed in)'));
      if (o.system === 'a10') {
        lines.push(meteredLine(mats.compression, [colour], sealMetres(staffRuns), 'A10 compression seal for the staff bead track'));
      }
      if (o.cord) lines.push(meteredLine(mats.cord, [], list.length * 10, 'Sash cord: about 10m per window'));
      return { lines: lines, plans: plans, notes: notes };
    }

    function beadLine(mat, want, need, spare, purpose, res, len) {
      var c = cheapestCover(need, offers(mat, want));
      return { mat: mat, purpose: purpose, need: need, unit: 'length', spareAdded: spare, cover: c, len: len, metresNet: res ? res.plan.reduce(function (s, b) { return s + b.cuts.reduce(function (t, p) { return t + p.len; }, 0); }, 0) : 0 };
    }
    function meteredLine(mat, want, metres, purpose) {
      return { mat: mat, purpose: purpose, need: metres, unit: 'm', cover: cheapestCover(metres, offers(mat, want)) };
    }
    function kitLine(mat, n, purpose, want) {
      return { mat: mat, purpose: purpose, need: n, unit: 'kit', cover: cheapestCover(n, offers(mat, want)) };
    }
    function total(lines) {
      var t = 0, bad = false;
      lines.forEach(function (l) { if (!l.need) return; if (!l.cover) bad = true; else t += l.cover.cost; });
      return { cost: t, incomplete: bad };
    }

    // Casements and doors (same in both scenarios)
    var other = [], otherPlans = [], otherNotes = [];
    if (cas.length) {
      var casKit = cas.filter(kitFits), casBig = cas.filter(function (w) { return !kitFits(w); });
      if (casKit.length) other.push(kitLine(mats.kit_casement, casKit.length, 'Casement and awning kit: one per sash up to 800 x 800mm'));
      if (casBig.length) {
        var pieces = [];
        casBig.forEach(function (w) { pieces = pieces.concat(cutList(w, o).perim); });
        var pm = mats.perimeter_seal, plen = (pm && pm.lengthMm) || 2100, pr = solve(pieces, plen);
        otherPlans.push({ key: 'perim', title: pm ? pm.title : 'Perimeter seal', stock: plen, res: pr });
        var l = beadLine(pm, [colour], pr.bars, 0, 'Perimeter seal for casements over 800 x 800mm or with natural timber', pr, plen);
        other.push(l);
        if (pr.oversize.length) otherNotes.push({ warn: true, text: pr.oversize.length + ' casement side(s) are longer than one ' + (plen / 1000).toFixed(1) + 'm length of perimeter seal. Call us.' });
      }
    }
    if (doors.length) {
      var dOk = doors.filter(function (w) { return w.w <= KIT.door.w && w.h <= KIT.door.h; });
      if (dOk.length) other.push(kitLine(mats.kit_door, dOk.length, 'Door draught seal kit: one per single door up to 2200 x 900mm'));
      if (dOk.length < doors.length) otherNotes.push({ warn: true, text: (doors.length - dOk.length) + ' door(s) are bigger than 2200 x 900mm. Call us for these.' });
    }

    // Double hung: loose parts for every window, or kits where a kit fits.
    var parts = partsDH(dh);
    var kitWins = dh.filter(dhKitOk), restWins = dh.filter(function (w) { return !dhKitOk(w); });
    var kitScenario = null;
    if (kitWins.length && mats.kit_dh) {
      var kp = partsDH(restWins);
      var kl = kitLine(mats.kit_dh, kitWins.length, 'Double hung draught sealing kit: beads, brush, meeting rail track and cord for one window up to 2000 x 1000mm');
      kitScenario = { lines: [kl].concat(kp.lines), plans: kp.plans, notes: kp.notes, kitCount: kitWins.length };
    }
    var sc = { parts: { lines: parts.lines.concat(other), plans: parts.plans.concat(otherPlans), notes: parts.notes.concat(otherNotes) } };
    sc.parts.total = total(sc.parts.lines);
    if (kitScenario) {
      sc.kit = { lines: kitScenario.lines.concat(other), plans: kitScenario.plans.concat(otherPlans), notes: kitScenario.notes.concat(otherNotes), kitCount: kitScenario.kitCount };
      sc.kit.total = total(sc.kit.lines);
    }
    var rec = sc.kit && !sc.kit.total.incomplete && (sc.parts.total.incomplete || sc.kit.total.cost <= sc.parts.total.cost) ? 'kit' : 'parts';

    if (dh.length && o.system === 'brush' && o.gap === 'unsure') issues.push('We have assumed 8.5mm brush, which suits a 5 to 7mm gap. Check the gap with a drill bit if you can.');
    if (dh.length && kitWins.length < dh.length && kitWins.length) issues.push((dh.length - kitWins.length) + ' window(s) do not suit the kit, so they are priced as loose parts.');
    return { windows: wins, counts: { dh: dh.length, casement: cas.length, door: doors.length }, scenarios: sc, recommended: rec, issues: issues, pile: pile, colour: colour };
  }

  return { A: A, KIT: KIT, PILE: PILE, parseLen: parseLen, cutList: cutList, sealMetres: sealMetres, solve: solve, lowerBound: lowerBound, cheapestCover: cheapestCover, packQty: packQty, offers: offers, plan: plan };
});

/* ---------------------------------------------------------------- UI */
(function () {
  'use strict';
  if (typeof document === 'undefined') return;
  var S = window.SSBeadSolver;

  function money(cents, cur) {
    try { return new Intl.NumberFormat('en-AU', { style: 'currency', currency: cur || 'AUD' }).format(cents / 100); }
    catch (e) { return '$' + (cents / 100).toFixed(2); }
  }
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  function track(name, params) {
    params = params || {};
    try { if (window.gtag) window.gtag('event', name, params); } catch (e) {}
    try { if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) Shopify.analytics.publish(name, params); } catch (e) {}
  }
  function b64e(obj) {
    var s = unescape(encodeURIComponent(JSON.stringify(obj)));
    return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
  function b64d(str) {
    str = str.replace(/-/g, '+').replace(/_/g, '/');
    while (str.length % 4) str += '=';
    return JSON.parse(decodeURIComponent(escape(atob(str))));
  }
  var DEFAULT_OPT = { system: 'brush', depth: 20, parting: 'timber', finish: 'painted', gap: 'unsure', meeting: true, cord: true, sill: false, spare: false };
  var TYPES = { dh: 'Double hung', casement: 'Casement / awning', door: 'Door' };
  var uid = 0;

  function init(el) {
    var island = el.querySelector('script[data-ssbc-data]');
    if (!island) return;
    var D;
    try { D = JSON.parse(island.textContent); } catch (e) { return; }
    var cur = D.currency || 'AUD';
    var $ = function (sel) { return el.querySelector(sel); };
    var listEl = $('[data-ssbc-list]'), outEl = $('[data-ssbc-out]'), plansEl = $('[data-ssbc-plans]');
    var stickyEl = $('[data-ssbc-sticky]'), liveEl = $('[data-ssbc-live]');
    var job = { opt: Object.assign({}, DEFAULT_OPT), windows: [] };
    var started = false, completed = false, mode = null, last = null, drawFor = 0;

    function newWin(w) { return Object.assign({ id: ++uid, label: '', type: 'dh', w: '', h: '', qty: 1 }, w || {}); }

    // ---- Load: share link, then draft, then one empty row.
    function load() {
      var h = location.hash.match(/[#&]j=([A-Za-z0-9_-]+)/);
      var src = null;
      if (h) { try { src = b64d(h[1]); } catch (e) {} }
      if (!src) { try { src = JSON.parse(localStorage.getItem('ssbc:draft:v1') || 'null'); } catch (e) {} }
      if (src && src.v === 1) {
        Object.keys(DEFAULT_OPT).forEach(function (k) { if (src.o && src.o[k] !== undefined) job.opt[k] = src.o[k]; });
        (src.w || []).slice(0, 80).forEach(function (r) {
          job.windows.push(newWin({ label: String(r[0] || '').slice(0, 40), type: TYPES[r[1]] ? r[1] : 'dh', w: String(r[2] || ''), h: String(r[3] || ''), qty: r[4] || 1 }));
        });
      }
      if (!job.windows.length) job.windows.push(newWin());
    }
    function pack() {
      return { v: 1, o: job.opt, w: job.windows.map(function (w) { return [w.label, w.type, w.w, w.h, w.qty]; }) };
    }
    var saveT;
    function save() {
      clearTimeout(saveT);
      saveT = setTimeout(function () { try { localStorage.setItem('ssbc:draft:v1', JSON.stringify(pack())); } catch (e) {} }, 400);
    }

    // ---- Window rows
    function hint(v) {
      var mm = S.parseLen(v);
      if (v === '' || v == null) return '';
      if (!(mm > 0)) return '<span class="ssbc-err">Enter a size in mm, e.g. 900</span>';
      if (mm < 300 && /^\d+(\.\d+)?$/.test(String(v).trim())) {
        var n = parseFloat(v);
        return '<span class="ssbc-err">' + esc(v) + 'mm looks small. Did you mean ' +
          '<button type="button" class="ssbc-fix" data-fix="' + Math.round(n * 10) + '">' + Math.round(n * 10) + 'mm</button> (cm) or ' +
          '<button type="button" class="ssbc-fix" data-fix="' + Math.round(n * 25.4) + '">' + Math.round(n * 25.4) + 'mm</button> (inches)?</span>';
      }
      if (mm > 4500) return '<span class="ssbc-err">That is over 4.5m. Check the size.</span>';
      if (!/^\d+$/.test(String(v).trim())) return '<span class="ssbc-ok">= ' + mm + 'mm</span>';
      return '';
    }
    function rowHTML(w, i) {
      var id = 'ssbc' + w.id;
      var types = Object.keys(TYPES).map(function (k) { return '<option value="' + k + '"' + (w.type === k ? ' selected' : '') + '>' + TYPES[k] + '</option>'; }).join('');
      return '<li class="ssbc-win" data-id="' + w.id + '">' +
        '<div class="ssbc-win__top"><span class="ssbc-win__n" aria-hidden="true">' + (i + 1) + '</span>' +
        '<label class="ssbc-vh" for="' + id + 'l">Label for window ' + (i + 1) + '</label>' +
        '<input class="ssbc-in ssbc-in--label" id="' + id + 'l" data-f="label" value="' + esc(w.label) + '" placeholder="Room (optional)" autocomplete="off" maxlength="40">' +
        '<label class="ssbc-vh" for="' + id + 't">Type of window ' + (i + 1) + '</label>' +
        '<select class="ssbc-in ssbc-in--type" id="' + id + 't" data-f="type">' + types + '</select></div>' +
        '<div class="ssbc-win__dims">' +
        '<div class="ssbc-fld"><label for="' + id + 'w">Width</label><div class="ssbc-unit"><input class="ssbc-in" id="' + id + 'w" data-f="w" value="' + esc(w.w) + '" inputmode="decimal" enterkeyhint="next" autocomplete="off" placeholder="900" aria-describedby="' + id + 'wh"><span aria-hidden="true">mm</span></div><div class="ssbc-hint" id="' + id + 'wh" aria-live="polite">' + hint(w.w) + '</div></div>' +
        '<div class="ssbc-fld"><label for="' + id + 'h">Height</label><div class="ssbc-unit"><input class="ssbc-in" id="' + id + 'h" data-f="h" value="' + esc(w.h) + '" inputmode="decimal" enterkeyhint="next" autocomplete="off" placeholder="1600" aria-describedby="' + id + 'hh"><span aria-hidden="true">mm</span></div><div class="ssbc-hint" id="' + id + 'hh" aria-live="polite">' + hint(w.h) + '</div></div>' +
        '<div class="ssbc-fld ssbc-fld--qty"><span class="ssbc-lab" id="' + id + 'ql">How many</span><div class="ssbc-qty" role="group" aria-labelledby="' + id + 'ql">' +
        '<button type="button" data-q="-1" aria-label="One fewer of window ' + (i + 1) + '">&minus;</button>' +
        '<input class="ssbc-in" data-f="qty" value="' + esc(w.qty) + '" inputmode="numeric" aria-label="Quantity of window ' + (i + 1) + '">' +
        '<button type="button" data-q="1" aria-label="One more of window ' + (i + 1) + '">+</button></div></div></div>' +
        '<div class="ssbc-win__acts"><button type="button" class="ssbc-txtbtn" data-act="dup">Duplicate</button>' +
        (job.windows.length > 1 ? '<button type="button" class="ssbc-txtbtn" data-act="del" aria-label="Remove window ' + (i + 1) + '">Remove</button>' : '') +
        '</div></li>';
    }
    function renderList(focusId) {
      listEl.innerHTML = job.windows.map(rowHTML).join('');
      if (focusId) { var f = listEl.querySelector('[data-id="' + focusId + '"] [data-f="w"]'); if (f) f.focus(); }
    }
    function winById(id) { return job.windows.filter(function (w) { return w.id === id; })[0]; }

    listEl.addEventListener('input', function (e) {
      var li = e.target.closest('.ssbc-win'), f = e.target.getAttribute('data-f');
      if (!li || !f) return;
      var w = winById(+li.getAttribute('data-id'));
      w[f] = e.target.value;
      if (f === 'w' || f === 'h') {
        var hEl = li.querySelector('#ssbc' + w.id + f + 'h');
        if (hEl) hEl.innerHTML = '';
      }
      schedule();
    });
    listEl.addEventListener('change', function (e) {
      var li = e.target.closest('.ssbc-win'), f = e.target.getAttribute('data-f');
      if (!li || !f) return;
      var w = winById(+li.getAttribute('data-id'));
      w[f] = e.target.value;
      if (f === 'w' || f === 'h') {
        var hEl = li.querySelector('#ssbc' + w.id + f + 'h');
        if (hEl) hEl.innerHTML = hint(w[f]);
      }
      if (f === 'qty') { w.qty = Math.max(1, Math.min(99, parseInt(w.qty, 10) || 1)); e.target.value = w.qty; }
      schedule();
    });
    listEl.addEventListener('click', function (e) {
      var li = e.target.closest('.ssbc-win');
      if (!li) return;
      var w = winById(+li.getAttribute('data-id')), i = job.windows.indexOf(w);
      var fix = e.target.closest('.ssbc-fix');
      if (fix) {
        var inp = fix.closest('.ssbc-fld').querySelector('input');
        w[inp.getAttribute('data-f')] = fix.getAttribute('data-fix');
        inp.value = w[inp.getAttribute('data-f')];
        fix.closest('.ssbc-hint').innerHTML = '';
        track('calc_unit_fix', { calc: 'bead' });
        inp.focus(); schedule(); return;
      }
      var q = e.target.closest('[data-q]');
      if (q) {
        w.qty = Math.max(1, Math.min(99, (parseInt(w.qty, 10) || 1) + (+q.getAttribute('data-q'))));
        li.querySelector('[data-f="qty"]').value = w.qty; schedule(); return;
      }
      var act = e.target.closest('[data-act]');
      if (!act) return;
      if (act.getAttribute('data-act') === 'dup') {
        var c = newWin({ label: w.label, type: w.type, w: w.w, h: w.h, qty: w.qty });
        job.windows.splice(i + 1, 0, c); renderList(c.id);
        track('calc_window_added', { window_count: job.windows.length, how: 'duplicate' });
      } else {
        job.windows.splice(i, 1); renderList();
        var next = listEl.querySelectorAll('.ssbc-win [data-f="w"]')[Math.max(0, i - 1)]; if (next) next.focus();
      }
      schedule();
    });
    $('[data-ssbc-add]').addEventListener('click', function () {
      var prev = job.windows[job.windows.length - 1];
      var w = newWin({ type: prev ? prev.type : 'dh' });
      job.windows.push(w); renderList(w.id);
      track('calc_window_added', { window_count: job.windows.length, how: 'add' });
      schedule();
    });

    // ---- Paste a list from a spreadsheet
    var pasteBox = $('[data-ssbc-paste]');
    $('[data-ssbc-paste-go]').addEventListener('click', function () {
      var rows = [], txt = pasteBox.value || '';
      txt.split(/\r?\n/).forEach(function (line) {
        if (!line.trim()) return;
        var cells = line.split(/\t|,|;|\s+x\s+|\s*[x×]\s*(?=\d)/i).map(function (c) { return c.trim(); }).filter(function (c) { return c !== ''; });
        var nums = [], label = [], type = 'dh';
        cells.forEach(function (c) {
          if (/^(dh|double)/i.test(c)) type = 'dh';
          else if (/^(case|awn)/i.test(c)) type = 'casement';
          else if (/^door/i.test(c)) type = 'door';
          else if (S.parseLen(c) > 0) nums.push(c);
          else label.push(c);
        });
        if (nums.length >= 2) rows.push(newWin({ label: label.join(' ').slice(0, 40), type: type, w: nums[0], h: nums[1], qty: nums[2] && parseInt(nums[2], 10) < 100 ? parseInt(nums[2], 10) : 1 }));
      });
      var msg = $('[data-ssbc-paste-msg]');
      if (!rows.length) { msg.textContent = 'No sizes found. Put width and height on each line, e.g. "Kitchen, 900, 1600, 2".'; return; }
      var empty = job.windows.filter(function (w) { return !w.w && !w.h; });
      job.windows = job.windows.filter(function (w) { return empty.indexOf(w) === -1; }).concat(rows).slice(0, 80);
      renderList(); pasteBox.value = '';
      msg.textContent = 'Added ' + rows.length + ' window' + (rows.length > 1 ? 's' : '') + '.';
      track('calc_window_added', { window_count: job.windows.length, how: 'paste' });
      schedule();
    });

    // ---- Options
    var optEl = $('[data-ssbc-opts]');
    function syncOpts() {
      optEl.querySelectorAll('input[name]').forEach(function (inp) {
        var k = inp.name.replace(/^ssbc-/, ''), v = job.opt[k];
        if (inp.type === 'checkbox') inp.checked = !!v;
        else inp.checked = String(v) === inp.value;
      });
      optEl.querySelectorAll('[data-show-if]').forEach(function (n) {
        var p = n.getAttribute('data-show-if').split('='); n.hidden = String(job.opt[p[0]]) !== p[1];
      });
    }
    optEl.addEventListener('change', function (e) {
      var inp = e.target; if (!inp.name) return;
      var k = inp.name.replace(/^ssbc-/, '');
      job.opt[k] = inp.type === 'checkbox' ? inp.checked : (k === 'depth' ? +inp.value : inp.value);
      syncOpts(); schedule();
    });

    // ---- Results
    var t;
    function schedule() { clearTimeout(t); t = setTimeout(run, 150); save(); }
    function lineHTML(l) {
      if (!l.need) return '';
      var m = l.mat, name = m ? m.title : 'Not set up';
      var needTxt = l.unit === 'length' ? l.need + ' length' + (l.need > 1 ? 's' : '') + ' of ' + (l.len / 1000).toFixed(1) + 'm' : l.unit === 'm' ? l.need + 'm' : l.need + ' kit' + (l.need > 1 ? 's' : '');
      var buy, cost;
      if (!m) { buy = '<span class="ssbc-err">Product not linked yet</span>'; cost = '–'; }
      else if (!l.cover) { buy = '<span class="ssbc-err">Out of stock in this size and colour right now.</span> <a class="ssbc-a" href="' + esc(m.url) + '">See options</a>'; cost = '–'; }
      else {
        buy = l.cover.buy.map(function (b) { return b.n + ' &times; ' + esc(b.offer.title) + ' <span class="ssbc-muted">(' + money(b.offer.price, cur) + ' each)</span>'; }).join('<br>');
        if (l.cover.spare > 0) {
          var unitWord = l.unit === 'length' ? 'length' : l.unit === 'm' ? 'm' : 'kit';
          buy += '<br><span class="ssbc-save">Buying ' + l.cover.units + (l.unit === 'm' ? 'm' : '') + ' works out cheaper than exactly ' + l.need + (l.unit === 'm' ? 'm' : '') + ', so you get ' + l.cover.spare + (l.unit === 'm' ? 'm' : ' spare ' + unitWord + (l.cover.spare > 1 ? 's' : '')) + ' extra.</span>';
        }
        if (l.spareAdded) buy += '<br><span class="ssbc-muted">Includes ' + l.spareAdded + ' spare length' + (l.spareAdded > 1 ? 's' : '') + ' for breakage.</span>';
        cost = money(l.cover.cost, cur);
      }
      var img = m && m.image ? '<img src="' + esc(m.image) + '" alt="" width="56" height="56" loading="lazy">' : '';
      return '<tr' + (!l.cover ? ' class="is-out"' : '') + '><td class="ssbc-bom__p">' + img + '<div><a class="ssbc-a" href="' + esc(m ? m.url : '#') + '">' + esc(name) + '</a><div class="ssbc-muted">' + esc(l.purpose) + '</div></div></td>' +
        '<td>' + needTxt + '</td><td>' + buy + '</td><td class="ssbc-num">' + cost + '</td></tr>';
    }
    function cartLines(sc) {
      var items = [];
      sc.lines.forEach(function (l) { if (l.need && l.cover) l.cover.buy.forEach(function (b) { items.push({ id: b.offer.id, quantity: b.n, title: (l.mat ? l.mat.title : '') + ' / ' + b.offer.title, price: b.offer.price, stock: b.offer.stock }); }); });
      return items;
    }
    function barsSVG(p) {
      var r = p.res, stock = p.stock;
      if (!r.bars) return '';
      var rows = r.plan.map(function (b, i) {
        var segs = b.cuts.map(function (c) {
          var w = (c.len / stock) * 100;
          return '<span class="ssbc-cut" style="flex-basis:' + w.toFixed(2) + '%" title="' + esc(c.win + ': ' + c.tag + ' ' + c.len + 'mm') + '">' + (w > 11 ? c.len : '') + '</span>';
        }).join('');
        return '<div class="ssbc-bar"><span class="ssbc-bar__n">' + (i + 1) + '</span><div class="ssbc-bar__l" role="img" aria-label="Length ' + (i + 1) + ': cuts ' + b.cuts.map(function (c) { return c.len; }).join(', ') + 'mm, offcut ' + b.offcut + 'mm">' + segs + '</div><span class="ssbc-bar__off">' + b.offcut + 'mm left</span></div>';
      }).join('');
      var head = '<h3 class="ssbc-h3">' + esc(p.title) + ': ' + r.bars + ' &times; ' + (stock / 1000).toFixed(1) + 'm</h3>' +
        '<p class="ssbc-muted">' + (r.proven ? 'This is the fewest lengths possible for these cuts.' : 'Within ' + (r.bars - r.lowerBound) + ' length' + (r.bars - r.lowerBound === 1 ? '' : 's') + ' of the theoretical minimum.') + ' Each cut allows 3mm for the saw.</p>';
      var table = '<table class="ssbc-cuts"><caption class="ssbc-vh">Cut list for ' + esc(p.title) + '</caption><thead><tr><th>Length</th><th>Window</th><th>Piece</th><th class="ssbc-num">Cut (mm)</th></tr></thead><tbody>' +
        r.plan.map(function (b, i) { return b.cuts.map(function (c, j) { return '<tr><td>' + (j ? '' : i + 1) + '</td><td>' + esc(c.win) + '</td><td>' + esc(c.tag) + '</td><td class="ssbc-num">' + c.len + '</td></tr>'; }).join(''); }).join('') + '</tbody></table>';
      return '<div class="ssbc-plan">' + head + '<div class="ssbc-bars">' + rows + '</div><details class="ssbc-det"><summary>Cut sheet, piece by piece</summary>' + table + '</details></div>';
    }
    function windowSVG(w, o) {
      if (!w || w.type !== 'dh') return '';
      var c = S.cutList(w, o), sj = c.staff[0].len, sh = c.staff[2].len, pj = c.parting[0].len, ph = c.parting[c.parting.length - 1].len;
      return '<figure class="ssbc-fig"><svg viewBox="0 0 300 360" role="img" aria-labelledby="ssbcFigT"><title id="ssbcFigT">Where each bead goes on ' + esc(w.label) + ', ' + w.w + ' by ' + w.h + 'mm</title>' +
        '<rect x="40" y="30" width="220" height="300" class="ssbc-f-frame"/>' +
        '<rect x="56" y="46" width="188" height="140" class="ssbc-f-sash"/><rect x="56" y="180" width="188" height="140" class="ssbc-f-sash"/>' +
        '<rect x="40" y="30" width="220" height="10" class="ssbc-f-staff"/><rect x="40" y="30" width="10" height="300" class="ssbc-f-staff"/><rect x="250" y="30" width="10" height="300" class="ssbc-f-staff"/><rect x="40" y="320" width="220" height="10" class="ssbc-f-staff"/>' +
        '<rect x="52" y="41" width="196" height="4" class="ssbc-f-part"/><rect x="52" y="41" width="4" height="289" class="ssbc-f-part"/><rect x="244" y="41" width="4" height="289" class="ssbc-f-part"/>' +
        '<rect x="56" y="180" width="188" height="5" class="ssbc-f-meet"/>' +
        '<text x="150" y="20" text-anchor="middle" class="ssbc-f-t">Head: staff ' + sh + ' · parting ' + ph + '</text>' +
        '<text x="20" y="180" text-anchor="middle" class="ssbc-f-t" transform="rotate(-90 20 180)">Sides: staff ' + sj + ' · parting ' + pj + '</text>' +
        (o.meeting ? '<text x="150" y="203" text-anchor="middle" class="ssbc-f-t ssbc-f-t--s">Meeting rail seal ' + w.w + '</text>' : '') +
        '<text x="150" y="352" text-anchor="middle" class="ssbc-f-t ssbc-f-t--s">Bottom: staff bead across the sill</text>' +
        '</svg><figcaption class="ssbc-legend"><span class="ssbc-key ssbc-key--staff"></span>Staff bead <span class="ssbc-key ssbc-key--part"></span>Parting bead' + (o.meeting ? ' <span class="ssbc-key ssbc-key--meet"></span>Meeting rail seal' : '') + '<br><span class="ssbc-muted">Cut sizes are the measured opening, to the long point of the mitre.</span></figcaption></figure>';
    }

    function run() {
      var res = S.plan(job, D);
      last = res;
      var n = res.windows.length;
      if (n && !started) { started = true; track('calc_started', { calc: 'bead' }); }
      if (!n) {
        outEl.innerHTML = '<p class="ssbc-empty">Enter the width and height of a window to see what you need.</p>';
        plansEl.innerHTML = ''; stickyEl.hidden = true; liveEl.textContent = ''; return;
      }
      var sc = res.scenarios, key = mode && sc[mode] ? mode : res.recommended, s = sc[key];
      var toggle = '';
      if (sc.kit) {
        var diff = sc.parts.total.cost - sc.kit.total.cost;
        toggle = '<div class="ssbc-choice" role="radiogroup" aria-label="Kits or loose parts">' +
          '<button type="button" role="radio" aria-checked="' + (key === 'kit') + '" data-mode="kit" class="ssbc-choice__b' + (key === 'kit' ? ' is-on' : '') + '"><strong>Kits</strong><span>' + money(sc.kit.total.cost, cur) + '</span>' + (res.recommended === 'kit' ? '<em>Cheapest</em>' : '') + '</button>' +
          '<button type="button" role="radio" aria-checked="' + (key === 'parts') + '" data-mode="parts" class="ssbc-choice__b' + (key === 'parts' ? ' is-on' : '') + '"><strong>Loose parts</strong><span>' + (sc.parts.total.incomplete ? 'Some items out of stock' : money(sc.parts.total.cost, cur)) + '</span>' + (res.recommended === 'parts' ? '<em>Cheapest</em>' : '') + '</button></div>' +
          '<p class="ssbc-muted">' + (diff > 0 && !sc.parts.total.incomplete ? (sc.kit.kitCount === 1 ? (n === 1 ? 'Your window suits' : '1 of your windows suits') : sc.kit.kitCount + ' of your windows suit') + ' our double hung kit, which works out ' + money(diff, cur) + ' cheaper than buying the same parts loose. ' : '') +
          'The kit has primed white beads, white brush, meeting rail track and cord for one window up to 2000 x 1000mm.</p>';
      }
      var notes = res.issues.map(function (x) { return '<li>' + esc(x) + '</li>'; }).concat(s.notes.map(function (x) { return '<li' + (x.warn ? ' class="ssbc-warn"' : '') + '>' + esc(x.text) + '</li>'; })).join('');
      var items = cartLines(s), count = items.reduce(function (a, b) { return a + b.quantity; }, 0);
      outEl.innerHTML = toggle +
        '<div class="ssbc-scroll"><table class="ssbc-bom"><caption class="ssbc-vh">Your materials list</caption><thead><tr><th>Product</th><th>You need</th><th>Cheapest way to buy it</th><th class="ssbc-num">Cost</th></tr></thead><tbody>' +
        s.lines.map(lineHTML).join('') + '</tbody><tfoot><tr><th colspan="3">Total for ' + n + ' ' + (n === 1 ? 'opening' : 'openings') + '</th><td class="ssbc-num"><strong>' + money(s.total.cost, cur) + '</strong></td></tr></tfoot></table></div>' +
        (notes ? '<ul class="ssbc-notes">' + notes + '</ul>' : '') +
        '<p class="ssbc-muted">Prices are today\'s shop prices. Your cart shows the final total, including any trade pricing.</p>' +
        '<div class="ssbc-acts"><button type="button" class="button button--primary ssbc-cta" data-ssbc-cart' + (items.length ? '' : ' disabled') + '>' + (count === 1 ? 'Add 1 item to cart' : 'Add all ' + count + ' items to cart') + '</button>' +
        '<button type="button" class="button button--secondary" data-ssbc-share>Copy link to this list</button>' +
        '<button type="button" class="button button--secondary" data-ssbc-print>Print cut sheet</button></div>' +
        '<p class="ssbc-status" data-ssbc-status role="status"></p>' +
        '<p class="ssbc-trade">Doing a bigger job? Send this list to <strong class="ssbc-sel">' + esc(D.salesEmail || 'sales@sellaseal.com.au') + '</strong> for a trade quote. <a class="ssbc-a" data-ssbc-mail href="#">Email it to us</a></p>';
      var dhWins = res.windows.filter(function (w) { return w.type === 'dh'; });
      if (drawFor >= dhWins.length) drawFor = 0;
      var pick = dhWins.length > 1 ? '<label class="ssbc-lab" for="ssbcPick">Show window</label> <select id="ssbcPick" class="ssbc-in ssbc-in--type" data-ssbc-pick>' + dhWins.map(function (w, i) { return '<option value="' + i + '"' + (i === drawFor ? ' selected' : '') + '>' + esc(w.label) + ' (' + w.w + ' x ' + w.h + ')</option>'; }).join('') + '</select>' : '';
      plansEl.innerHTML = (dhWins.length ? '<div class="ssbc-figwrap">' + pick + windowSVG(dhWins[drawFor], job.opt) + '</div>' : '') +
        s.plans.map(barsSVG).join('');
      stickyEl.hidden = !items.length;
      stickyEl.querySelector('[data-ssbc-sticky-t]').textContent = money(s.total.cost, cur) + ' · ' + count + ' item' + (count === 1 ? '' : 's');
      liveEl.textContent = 'Total ' + money(s.total.cost, cur) + ' for ' + n + ' openings.';
      if (!completed) { completed = true; setTimeout(function () { track('calc_completed', { calc: 'bead', window_count: n, value: s.total.cost / 100, currency: cur }); }, 1500); }
    }

    outEl.addEventListener('click', function (e) {
      var m = e.target.closest('[data-mode]');
      if (m) { mode = m.getAttribute('data-mode'); run(); return; }
      if (e.target.closest('[data-ssbc-cart]')) return addAll(e.target.closest('[data-ssbc-cart]'));
      if (e.target.closest('[data-ssbc-share]')) return share();
      if (e.target.closest('[data-ssbc-print]')) { track('calc_print', { calc: 'bead' }); return window.print(); }
      if (e.target.closest('[data-ssbc-mail]')) { e.preventDefault(); return mail(); }
    });
    plansEl.addEventListener('change', function (e) {
      if (e.target.matches('[data-ssbc-pick]')) { drawFor = +e.target.value; run(); var p = plansEl.querySelector('[data-ssbc-pick]'); if (p) p.focus(); }
    });
    stickyEl.querySelector('button').addEventListener('click', function () { var b = outEl.querySelector('[data-ssbc-cart]'); if (b) addAll(b); });

    function status(msg, err) {
      var s = outEl.querySelector('[data-ssbc-status]');
      if (s) { s.innerHTML = msg; s.classList.toggle('is-err', !!err); }
    }
    function current() { var sc = last.scenarios; return sc[mode && sc[mode] ? mode : last.recommended]; }
    function shareURL() { return location.origin + location.pathname + '#j=' + b64e(pack()); }
    function share() {
      var url = shareURL();
      try { history.replaceState(null, '', url); } catch (e) {}
      var done = function () { status('Link copied. Anyone with the link sees this list, re-priced on the day they open it.'); };
      if (navigator.share && matchMedia('(pointer: coarse)').matches) {
        navigator.share({ title: 'Sellaseal bead list', url: url }).then(done).catch(function () {});
      } else if (navigator.clipboard) navigator.clipboard.writeText(url).then(done, function () { status('Copy this link: ' + esc(url)); });
      else status('Copy this link: ' + esc(url));
      track('calc_share', { calc: 'bead' });
    }
    function listText() {
      var s = current(), lines = [];
      lines.push('Windows:');
      job.windows.forEach(function (w, i) { if (S.parseLen(w.w) > 0 && S.parseLen(w.h) > 0) lines.push('- ' + (w.label || 'Window ' + (i + 1)) + ': ' + TYPES[w.type] + ', ' + S.parseLen(w.w) + ' x ' + S.parseLen(w.h) + 'mm x ' + w.qty); });
      lines.push('', 'Materials:');
      cartLines(s).forEach(function (it) { lines.push('- ' + it.quantity + ' x ' + it.title); });
      lines.push('', 'Shop total today: ' + money(s.total.cost, cur), 'List: ' + shareURL());
      return lines.join('\n');
    }
    function mail() {
      var to = D.salesEmail || 'sales@sellaseal.com.au';
      track('calc_trade_quote', { calc: 'bead' });
      location.href = 'mailto:' + to + '?subject=' + encodeURIComponent('Trade quote: bead list') + '&body=' + encodeURIComponent('Hi Sellaseal team,\n\nPlease quote this list.\n\n' + listText() + '\n\nBusiness name:\nABN:\nPhone:\n');
      status('If your email app did not open, email <strong>' + esc(to) + '</strong> and paste the link from "Copy link to this list".');
    }
    function addAll(btn) {
      var s = current(), items = cartLines(s);
      if (!items.length) return;
      var over = items.filter(function (it) { return it.stock != null && it.quantity > it.stock; });
      if (over.length) { status('We only have ' + over[0].stock + ' of ' + esc(over[0].title) + ' in stock. Call us or reduce the quantity.', true); return; }
      var jobId = Date.now().toString(36);
      btn.disabled = true; var orig = btn.textContent; btn.textContent = 'Adding…';
      var drawer = document.querySelector('cart-drawer');
      var body = { items: items.map(function (it) { return { id: it.id, quantity: it.quantity, properties: { _source: 'beading-calculator', _job: jobId } }; }) };
      if (drawer) { body.sections = ['cart-drawer', 'cart-icon-bubble']; body.sections_url = location.pathname; }
      fetch((D.root || '/') + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) })
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, j: j }; }); })
        .then(function (x) {
          if (!x.ok) throw new Error(x.j.description || x.j.message || 'Could not add to cart');
          track('calc_add_to_cart', { calc: 'bead', value: s.total.cost / 100, currency: cur, line_count: items.length });
          if (drawer && x.j.sections && typeof drawer.renderContents === 'function') {
            drawer.classList.remove('is-empty');
            drawer.renderContents(Object.assign({}, x.j, { id: items[0].id }));
          } else { location.href = (D.root || '/') + 'cart'; return; }
          // Dawn declares publish() and PUB_SUB_EVENTS as globals (a const is not on window).
          try { if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'beading-calculator', cartData: x.j }); } catch (e) {}
          status('Added to your cart. <a class="ssbc-a" href="' + (D.root || '/') + 'cart">View cart</a>');
        })
        .catch(function (err) { track('calc_add_error', { calc: 'bead', reason: String(err.message).slice(0, 80) }); status(esc(err.message) + '. Nothing was added, so try again or call us.', true); })
        .then(function () { btn.disabled = false; btn.textContent = orig; });
    }

    window.addEventListener('beforeprint', function () { el.querySelectorAll('.ssbc-plans details').forEach(function (d) { d.open = true; }); });
    load(); renderList(); syncOpts(); run();
    track('calc_view', { calc: 'bead' });
    window.addEventListener('hashchange', function () { job.windows = []; load(); renderList(); syncOpts(); run(); });
  }

  function boot() { document.querySelectorAll('[data-ssbc]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
